import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

const SINGLE_CLICK_DELAY_MS = 220;

const usePlotlyEvents = ({
  isLabelMode,
  layout,
  annotations,
  labelsRawRef,
  sectionsRef,
  tRawRef,
  setHoveredLabel,
  timerEtiquette,
  handlePhaseClickInternal,
  onSignalClick,
  onLabelHover,
  onLabelUnhover,
  onPhaseClick,
  signalCount,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const plotElRef = useRef(null);
  const pendingSignalClickTimer = useRef(null);

  const clearPendingSignalClick = useCallback(() => {
    if (pendingSignalClickTimer.current) {
      clearTimeout(pendingSignalClickTimer.current);
      pendingSignalClickTimer.current = null;
    }
  }, []);

  useEffect(() => () => clearPendingSignalClick(), [clearPendingSignalClick]);

  const activeLayout = useMemo(() => {
    if (hoveredIndex === null) return layout;
    const ann = annotations[hoveredIndex];
    return {
      ...layout,
      annotations: [...(layout.annotations || []), ...(ann ? [ann] : [])],
    };
  }, [layout, annotations, hoveredIndex]);

  const handleLabelHoverInternal = useCallback(
    (t, x, y) => {
      clearTimeout(timerEtiquette.current);
      const tNum = Number(t);
      const all = labelsRawRef.current;
      if (!all.length) return;
      const found = all.reduce((best, l) =>
        Math.abs(Number(l.t) - tNum) < Math.abs(Number(best.t) - tNum)
          ? l
          : best
      );
      if (found) setHoveredLabel({ label: found, x, y });
    },
    [setHoveredLabel]
  ); // labelsRawRef and timerEtiquette are refs — stable, no need in deps

  const handleLabelUnhoverInternal = useCallback(() => {
    timerEtiquette.current = setTimeout(() => setHoveredLabel(null), 300);
  }, [setHoveredLabel]);

  const getNearestPoint = useCallback(
    (e, signalOnly = false) => {
      const points = e?.points || [];
      if (!points.length) return null;

      const nSignal = signalCount || 1;
      const candidates = signalOnly
        ? points.filter((p) => p?.curveNumber < nSignal)
        : points;

      if (!candidates.length) return null;
      if (candidates.length === 1) return candidates[0];

      const mouseX = e?.event?.clientX;
      const mouseY = e?.event?.clientY;
      if (mouseX == null || mouseY == null) return candidates[0];

      let best = candidates[0];
      let bestDist = Number.POSITIVE_INFINITY;

      candidates.forEach((p) => {
        const xa = p?.xaxis;
        const ya = p?.yaxis;
        if (
          !xa ||
          !ya ||
          typeof xa.d2p !== 'function' ||
          typeof ya.d2p !== 'function' ||
          xa._offset == null ||
          ya._offset == null
        ) {
          return;
        }

        try {
          const px = xa._offset + xa.d2p(p.x);
          const py = ya._offset + ya.d2p(p.y);
          const d = Math.hypot(px - mouseX, py - mouseY);
          if (d < bestDist) {
            bestDist = d;
            best = p;
          }
        } catch (_) {
          // Ignore points with non-convertible coordinates.
        }
      });

      return best;
    },
    [signalCount]
  );

  const handleHover = useCallback(
    (e) => {
      const pt = getNearestPoint(e);
      if (!pt) return;

      const nSignal = signalCount || 1;

      if (pt.curveNumber < nSignal) {
        // Signal hover intentionally disabled: label creation is click-driven.
        return;
      }

      if (pt.customdata !== undefined) {
        if (pt.customdata?.type === 'phase') return;
        if (!plotElRef.current)
          plotElRef.current = e.event?.target?.closest?.('.js-plotly-plot');
        if (plotElRef.current) plotElRef.current.style.cursor = 'pointer';
        if (isLabelMode) {
          handleLabelHoverInternal(
            pt.customdata,
            e.event?.clientX,
            e.event?.clientY
          );
        } else if (onLabelHover) {
          onLabelHover(pt.customdata, e.event?.clientX, e.event?.clientY);
        }
        return;
      }

      setHoveredIndex(pt.curveNumber - nSignal);
    },
    [
      getNearestPoint,
      isLabelMode,
      handleLabelHoverInternal,
      onLabelHover,
      signalCount,
    ]
  );

  const handleUnhover = useCallback(() => {
    setHoveredIndex(null);
    if (plotElRef.current) plotElRef.current.style.cursor = '';
    if (isLabelMode) {
      handleLabelUnhoverInternal();
    } else if (onLabelUnhover) {
      onLabelUnhover();
    }
  }, [isLabelMode, handleLabelUnhoverInternal, onLabelUnhover]);

  const handleClickAnnotation = useCallback(
    (e) => {
      if (e.index === undefined) return;
      const sec = sectionsRef.current[e.index];
      if (!sec) return;
      const phaseData = {
        type: 'phase',
        tStart: sec.tStart,
        tEnd: sec.tEnd,
        label: sec.label,
        color: sec.color,
      };
      if (isLabelMode) {
        handlePhaseClickInternal(phaseData);
      } else if (onPhaseClick) {
        onPhaseClick(phaseData, e.event?.clientX, e.event?.clientY);
      }
    },
    [isLabelMode, handlePhaseClickInternal, onPhaseClick]
  );

  const handleClick = useCallback(
    (e) => {
      const clickDetail = Number(e?.event?.detail || 1);
      if (clickDetail > 1) {
        clearPendingSignalClick();
        return;
      }

      const nSignal = signalCount || 1;
      const signalPt = getNearestPoint(e, true);
      if (signalPt && signalPt.curveNumber < nSignal) {
        const ts = tRawRef.current?.[signalPt.pointNumber];
        clearPendingSignalClick();
        pendingSignalClickTimer.current = setTimeout(() => {
          onSignalClick?.({
            ts: Number(ts ?? signalPt.x),
            yVal: signalPt.y,
            curveIndex: signalPt.curveNumber,
            formula: signalPt.data?.meta?.formula || null,
          });
          pendingSignalClickTimer.current = null;
        }, SINGLE_CLICK_DELAY_MS);
        return;
      }

      const pt = getNearestPoint(e);
      if (!pt) return;

      if (pt.customdata?.type !== 'phase') return;
      if (isLabelMode) {
        handlePhaseClickInternal(pt.customdata);
      } else if (onPhaseClick) {
        onPhaseClick(pt.customdata, e.event?.clientX, e.event?.clientY);
      }
    },
    [
      clearPendingSignalClick,
      getNearestPoint,
      isLabelMode,
      handlePhaseClickInternal,
      onSignalClick,
      onPhaseClick,
      signalCount,
      tRawRef,
    ]
  );

  const handleDoubleClick = useCallback(() => {
    clearPendingSignalClick();
  }, [clearPendingSignalClick]);

  return {
    activeLayout,
    handleHover,
    handleUnhover,
    handleClick,
    handleDoubleClick,
    handleClickAnnotation,
  };
};

export default usePlotlyEvents;
